import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { DepositStatusComponent } from '@connect-services/client/components/deposit-status/deposit-status.component';
import {
    CreateEditDataModalComponent,
    CreateEditDataModalForm,
} from '@connect-services/client/components/modals/create-edit-data-modal/create-edit-data-modal.component';
import {
    AddDepositModalComponent,
    AddDepositModalForm,
} from '@connect-services/client/components/modals/deposit/add-deposit-modal/add-deposit-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositFolderTableRow,
    ManagementDataService,
} from '@connect-services/client/services/management-data.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { CustomColumn } from '@connect-services/src/lib/components/table/table.component';
import { Deposit, DepositFolder, DepositFoldersService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DepositStatusComponent, RessourcesModule],
    selector: 'app-deposit-row-item',
    styleUrl: './deposit-row-item.component.scss',
    templateUrl: './deposit-row-item.component.html',
})
export class DepositRowItemComponent {
    public readonly column: InputSignal<CustomColumn> = input.required();
    public readonly depositFolder: InputSignal<DepositFolderTableRow> = input.required();

    protected readonly Deposit = Deposit;
    protected readonly lastDeposit = computed<Deposit | undefined>(() => {
        return this.managementDataService.findLastDeposit(this.depositFolder());
    });
    protected readonly managementDataService = inject(ManagementDataService);
    protected readonly translateService = inject(TranslateService);

    private readonly depositBloc = inject(DepositBloc);
    private readonly depositFoldersService = inject(DepositFoldersService);
    private readonly matDialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    protected abortImport(event: MouseEvent): void {
        event.stopPropagation();

        const deposit = this.lastDeposit();

        if (!deposit) {
            return;
        }

        this.managementDataService.openAbortDepositImportConfirmation(deposit);
    }

    protected openAddDepositModal(depositFolder: DepositFolderTableRow): void {
        const dataFolder: DepositFolder = this.managementDataService.findDepositFolder(depositFolder);
        this.managementDataService.toggleExpandedElement(depositFolder.id);

        const dialogRef = this.matDialog.open(AddDepositModalComponent, {
            autoFocus: false,
            data: {
                depositCount: dataFolder.depositCount ?? 0,
                depositFolder: dataFolder,
                standard: dataFolder?.standard,
                title: 'addDeposit',
            },
            width: '40vw',
        });

        dialogRef.afterClosed().subscribe(async (response: AddDepositModalForm | null) => {
            if (!response?.value.name) {
                return;
            }

            await this.managementDataService.createDeposit(response);
        });
    }

    protected openDeleteData(depositFolder: DepositFolderTableRow): void {
        this.managementDataService.toggleExpandedElement(depositFolder.id);

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('allDepositsWillBeDeleted'),
                title: this.translocoService.translate('confirmDeleteData'),
                type: ModalType.DANGER,
            },
            width: '25rem',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            try {
                await firstValueFrom(
                    this.depositFoldersService.depositFolderControllerDelete({
                        id: depositFolder.id,
                    }),
                );

                await this.closeDetailPanelIfNeeded(depositFolder.id);

                this.snackBarService.openSuccessSnackBar('dataDeletedSuccess');

                await this.managementDataService.refreshDepositFolderList();
            } catch {
                this.snackBarService.openErrorSnackBar('datatDeletedError');
            }
        });

        return;
    }

    protected openUpdateData(depositFolder: DepositFolderTableRow): void {
        const dataFolder: DepositFolder = this.managementDataService.findDepositFolder(depositFolder);
        this.managementDataService.toggleExpandedElement(depositFolder.id);

        const dialogRef = this.matDialog.open(CreateEditDataModalComponent, {
            autoFocus: false,
            data: { isEditMode: true, name: depositFolder.name, title: 'updateData' },
            width: '20vw',
        });

        dialogRef.afterClosed().subscribe(async (response: CreateEditDataModalForm | null) => {
            if (!response?.value.name) {
                return;
            }

            await this.managementDataService.updateDepositFolder(response, dataFolder);
        });
    }

    private async closeDetailPanelIfNeeded(depositFolderId: string): Promise<void> {
        if (!this.depositBloc.value() || this.managementDataService.expandedElementId() !== depositFolderId) {
            return;
        }

        if (this.managementDataService.expandedElementId() === this.depositBloc.value()!.depositFolder) {
            await this.router.navigate([...RouteCompletePathEnum.ORGANIZATION_DATA]);
        }
    }
}
